import { Component, forwardRef, Input } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { getCountryForTimezone } from 'countries-and-timezones';
import {
  BusinessObject,
  IsoCountryCodeWithPhoneCountryCode,
} from '../../models';
import { ControlValueAccessorComponent } from '../../core';
import { validatorAgain } from '../../validators';
import { StringUtils } from '../../utils';
import {
  AbstractStorageService,
  BreakpointService,
  ErrorHandlerServiceService,
  SmsService,
} from '../../services';

@Component({
  selector: 'lib-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true,
    },
  ],
})
export class PhoneNumberInputComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  @Input() set businessObject(value: BusinessObject) {
    this.fetchAllAvailableIsoCodes(value);
  }

  @Input() set isRequired(value: boolean) {
    if (value) {
      this.controlPhoneCountryCode.setValidators([
        Validators.required,
        Validators.pattern(/^\+[0-9]+$/),
      ]);
      this.controlPhoneNr.setValidators([
        Validators.required,
        validatorAgain(this.FORM_NAME.PHONE_COUNTRY_CODE),
      ]);
    } else {
      this.controlPhoneCountryCode.setValidators([
        Validators.pattern(/^\+[0-9]+$/),
      ]);
      this.controlPhoneNr.setValidators([
        validatorAgain(this.FORM_NAME.PHONE_COUNTRY_CODE),
      ]);
    }

    this.submitForm.updateValueAndValidity();
  }

  public readonly FORM_NAME = {
    PHONE_COUNTRY_CODE: 'phoneCountryCode',
    PHONE_NR: 'phoneNr',
  };

  public allAvailableIsoCodes: IsoCountryCodeWithPhoneCountryCode[] = [];
  public filteredIsoCodes: IsoCountryCodeWithPhoneCountryCode[] = [];

  public submitForm: FormGroup;

  public isValid = true;

  private originalPhoneNr: string;
  private defaultIsoCode: IsoCountryCodeWithPhoneCountryCode;

  constructor(
    public breakpointService: BreakpointService,
    protected storageService: AbstractStorageService,
    protected errorHandlerServiceService: ErrorHandlerServiceService,
    protected smsService: SmsService
  ) {
    super();

    this.submitForm = new FormGroup({
      [this.FORM_NAME.PHONE_COUNTRY_CODE]: new FormControl<string>(
        this.storageService.getLastSelectedCountryCode() || null,
        [Validators.required, Validators.pattern(/^\+[0-9]+$/)]
      ),
      [this.FORM_NAME.PHONE_NR]: new FormControl<number>(null, [
        Validators.required,
        validatorAgain(this.FORM_NAME.PHONE_COUNTRY_CODE),
      ]),
    });

    this.controlPhoneCountryCode.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((phoneCountryCode) => {
        if (phoneCountryCode) {
          const valueToFilter = StringUtils.simplifyString(phoneCountryCode);
          this.filteredIsoCodes = this.allAvailableIsoCodes?.filter(
            (i) =>
              i.phoneCountryCode?.includes(valueToFilter) ||
              StringUtils.simplifyString(i.isoCode)?.includes(valueToFilter)
          );
        } else {
          this.filteredIsoCodes = this.allAvailableIsoCodes.slice();
        }
      });

    this.submitForm.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(async (value) => {
        const isoCode = this.allAvailableIsoCodes?.find(
          (v) =>
            v.phoneCountryCode === value?.[this.FORM_NAME.PHONE_COUNTRY_CODE]
        );
        const phoneNr = value?.[this.FORM_NAME.PHONE_NR]
          ? `${value?.[this.FORM_NAME.PHONE_NR]}`
          : null;

        const validatedCharactersPhoneNr = phoneNr?.length
          ? StringUtils.removeAllNonNumberCharacters(phoneNr)
          : null;

        if (validatedCharactersPhoneNr !== phoneNr) {
          this.controlPhoneNr.setValue(Number(validatedCharactersPhoneNr));
        } else {
          if (!isoCode || !phoneNr?.length) {
            this.isValid = false;
            this._onChange(null);
          } else {
            const phoneNumberValidationResponse =
              await smsService.validatePhoneNumber(phoneNr, isoCode?.isoCode);

            this.isValid = phoneNumberValidationResponse?.valid;

            if (this.isValid) {
              this._onChange(phoneNumberValidationResponse?.phoneNr);
            } else {
              this._onChange(null);
            }
          }

          if (!this.isValid) {
            this.controlPhoneNr.markAsTouched();
            this.controlPhoneNr.setErrors({});
          } else {
            this.controlPhoneNr.markAsTouched();
            this.controlPhoneNr.setErrors(null);

            this.storageService.saveLastSelectedCountryCode(
              this.controlPhoneCountryCode.value
            );
          }
        }
      });

    this.fetchAllAvailableIsoCodes();
  }

  public pastePhoneNrEventHandler(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData('text');

    setTimeout(() => {
      this.controlPhoneNr.setValue(
        StringUtils.removeAllNonNumberCharacters(pastedText)
      );
    }, 50);
  }

  public get controlPhoneCountryCode(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.PHONE_COUNTRY_CODE);
  }

  public get controlPhoneNr(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.PHONE_NR);
  }

  writeValue(phoneNr: string) {
    this.originalPhoneNr = phoneNr;
    this.parseIsoCodeAndPhoneNumber(phoneNr);
  }

  private generatePhoneNumberToEmit(): string {
    return `${this.controlPhoneCountryCode?.value}${this.controlPhoneNr.value}`;
  }

  private async parseIsoCodeAndPhoneNumber(phoneNr: string) {
    if (!phoneNr?.length) {
      this.controlPhoneNr.setValue(null);
      return;
    }

    if (phoneNr === this.generatePhoneNumberToEmit()) {
      return;
    }

    let phoneNrAsNumber;
    try {
      phoneNrAsNumber = Number(phoneNr);
    } catch (e) {}

    if (!phoneNrAsNumber || phoneNrAsNumber < 0) {
      return;
    }
    let validatedPhoneNr;

    try {
      validatedPhoneNr = await this.smsService.validatePhoneNumber(
        phoneNr.startsWith('+') ? phoneNr : `${phoneNrAsNumber}`,
        phoneNr.startsWith('+') ? null : this.defaultIsoCode?.isoCode || null
      );
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }

    this.isValid = !!validatedPhoneNr?.valid;

    if (!this.isValid) {
      const matchedIsoCode = this.allAvailableIsoCodes?.find((ic) =>
        phoneNr?.startsWith(ic.phoneCountryCode)
      );
      this.controlPhoneCountryCode.setValue(
        matchedIsoCode?.phoneCountryCode ||
          this.defaultIsoCode?.phoneCountryCode
      );

      if (matchedIsoCode?.isoCode) {
        this.controlPhoneNr.setValue(
          phoneNr?.replace(matchedIsoCode?.phoneCountryCode, '')
        );
      } else {
        this.controlPhoneNr.setValue(phoneNrAsNumber);
      }

      this.controlPhoneNr.markAsDirty();
    } else {
      const matchedIsoCode = this.allAvailableIsoCodes?.find((ic) =>
        validatedPhoneNr?.phoneNr?.startsWith(ic.phoneCountryCode)
      );
      this.controlPhoneCountryCode.setValue(
        matchedIsoCode?.phoneCountryCode ||
          this.defaultIsoCode?.phoneCountryCode
      );

      if (matchedIsoCode?.isoCode) {
        this.controlPhoneNr.setValue(
          validatedPhoneNr?.phoneNr?.replace(
            matchedIsoCode?.phoneCountryCode,
            ''
          )
        );
      }
    }
  }

  private async fetchAllAvailableIsoCodes(businessObject?: BusinessObject) {
    this.allAvailableIsoCodes = await this.smsService.getAllCountryCodes();

    // set defaultIsoCode to BO locale if given
    this.defaultIsoCode = this.allAvailableIsoCodes?.find((i) =>
      businessObject?.locale?.toUpperCase().includes(i.isoCode.toUpperCase())
    );

    if (!this.defaultIsoCode) {
      this.defaultIsoCode = this.generateIsoCodeFromCurrentBrowserTimezone(
        this.allAvailableIsoCodes
      );
    }

    if (
      !this.controlPhoneCountryCode.value &&
      !!this.defaultIsoCode?.phoneCountryCode
    ) {
      this.controlPhoneCountryCode.setValue(
        this.defaultIsoCode?.phoneCountryCode
      );
    }

    this.parseIsoCodeAndPhoneNumber(this.originalPhoneNr);
  }

  private generateIsoCodeFromCurrentBrowserTimezone(
    countryCodeList: IsoCountryCodeWithPhoneCountryCode[]
  ): IsoCountryCodeWithPhoneCountryCode {
    try {
      const country = getCountryForTimezone(
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
      return countryCodeList?.find((c) => c.isoCode === country.id);
    } catch (e) {
      return null;
    }
  }
}
